import React, { useState, useEffect, useRef, useCallback } from 'react';

import { forwardRef, useImperativeHandle } from 'react';

// VideoPlayer simplificado - apenas renderiza o vídeo
const VideoPlayer = forwardRef(({ src, poster }, ref) => {
  const videoRef = useRef(null);

  // Expõe o elemento de vídeo diretamente
  useImperativeHandle(ref, () => videoRef.current);

  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      playsInline
      muted
      loop
      preload="metadata"
      className="w-full h-full object-cover pointer-events-none"
    />
  );
});

const VideoCard = React.memo(({ video, index }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const videoRef = useRef(null);

  // Attach event listeners diretamente ao elemento de vídeo
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const handlePlaying = () => {
      setIsBuffering(false);
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleWaiting = () => {
      setIsBuffering(true);
    };

    videoEl.addEventListener('playing', handlePlaying);
    videoEl.addEventListener('pause', handlePause);
    videoEl.addEventListener('waiting', handleWaiting);

    return () => {
      videoEl.removeEventListener('playing', handlePlaying);
      videoEl.removeEventListener('pause', handlePause);
      videoEl.removeEventListener('waiting', handleWaiting);
    };
  }, []);

  const handleTogglePlay = (e) => {
    e.stopPropagation();
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (videoEl.paused) {
      videoEl.play().catch((err) => {
        console.log('Erro no play:', err);
      });
    } else {
      videoEl.pause();
    }
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden bg-[#022895]/20 backdrop-blur-xl border border-[#A4D7D2]/10 aspect-[9/16] cursor-pointer group hover:border-[#A4D7D2]/30 transition-all"
      onClick={handleTogglePlay}
    >
      <VideoPlayer
        ref={videoRef}
        src={video.src}
        poster={video.poster}
      />

      {isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-20">
          <div className="w-10 h-10 border-4 border-[#A4D7D2]/20 border-t-[#A4D7D2] rounded-full animate-spin" />
        </div>
      )}

      {!isPlaying && !isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <div className="w-16 h-16 rounded-full bg-[#A4D7D2]/20 backdrop-blur-lg flex items-center justify-center border-2 border-[#A4D7D2]/40 group-hover:bg-[#A4D7D2] transition-all">
            <i className="fas fa-play text-white text-lg ml-1 group-hover:text-[#0F1822]"></i>
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0F1822] to-transparent pointer-events-none">
        <span className="text-xs text-[#A4D7D2] font-mono">{video.tag}</span>
        <h3 className="text-sm font-bold text-white">{video.title}</h3>
      </div>
    </div>
  );
});

const VideosGrid = React.memo(({ videos }) => {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {videos.map((video, index) => (
        <VideoCard
          key={video.src}
          video={video}
          index={index}
        />
      ))}
    </div>
  );
});

const CountersSection = React.memo(() => {
  const [counter1, setCounter1] = useState(0);
  const [counter2, setCounter2] = useState(0);
  const [counter3, setCounter3] = useState(0);
  const animated = useRef(false);

  useEffect(() => {
    if (animated.current) return;
    animated.current = true;

    const targets = [15, 3, 5000];
    const setters = [setCounter1, setCounter2, setCounter3];
    const duration = 2000;
    const start = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);

      setters.forEach((set, i) => set(Math.floor(targets[i] * ease)));

      if (progress < 1) requestAnimationFrame(animate);
      else setters.forEach((set, i) => set(targets[i]));
    };

    setTimeout(() => requestAnimationFrame(animate), 500);
  }, []);

  return (
    <div className="grid grid-cols-3 gap-8 text-center">
      <div>
        <h3 className="text-5xl font-bold text-[#A4D7D2]">{counter1}+</h3>
        <p className="text-sm text-[#9FA4B1] mt-2">Anos de experiência</p>
      </div>
      <div>
        <h3 className="text-5xl font-bold text-[#A4D7D2]">{counter2}+</h3>
        <p className="text-sm text-[#9FA4B1] mt-2">Escolas parceiras</p>
      </div>
      <div>
        <h3 className="text-5xl font-bold text-[#A4D7D2]">{counter3}+</h3>
        <p className="text-sm text-[#9FA4B1] mt-2">Alunos impactados</p>
      </div>
    </div>
  );
});

// FadeUp movido para fora do componente para evitar recriação a cada render
const FadeUp = React.memo(({ children, delay = 0, isVisible, className = "", fadeRef }) => (
  <div
    ref={fadeRef}
    className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}
    style={{ transitionDelay: `${delay}ms` }}
  >
    {children}
  </div>
));

export default function CreativeSolutionWebsite() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeMethod, setActiveMethod] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, src: '', type: '' });
  const [visibleElements, setVisibleElements] = useState(new Set());

  const totalSlides = 4;
  const fadeRefs = useRef([]);

  // Debounce helper
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleScroll = useCallback(() => {
    fadeRefs.current.forEach((el, index) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.85) {
          setVisibleElements(prev => new Set([...prev, index]));
        }
      }
    });
  }, []);

  useEffect(() => {
    const debouncedScroll = debounce(handleScroll, 80);
    handleScroll(); // Inicial
    window.addEventListener('scroll', debouncedScroll);
    return () => window.removeEventListener('scroll', debouncedScroll);
  }, [handleScroll]);

  // Carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Lightbox escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightbox({ open: false, src: '', type: '' });
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const slides = [
    { img: '/media/foto-scratch.jpg', label: 'PROGRAMAÇÃO', desc: 'Lógica Visual em Computadores' },
    { img: '/media/foto-montagem.jpg', label: 'CULTURA MAKER', desc: 'Mãos na Massa' },
    { img: '/media/foto-equipe.jpg', label: 'COLABORAÇÃO', desc: 'Trabalho em Equipe' },
    { img: '/media/foto-robo.jpg', label: 'ROBÓTICA', desc: 'Robótica Educacional' },
  ];

  const videos = [
    { src: '/media/video-robo-otimizado-v2.mp4', poster: '/media/thumb-robo.jpg', tag: 'Kits de Automação Básica', title: 'Robô Autônomo' },
    { src: '/media/video-robos-teste-otimizado-v2.mp4', poster: '/media/thumb-robos-teste.jpg', tag: 'Robótica de Alta Performance', title: 'Teste de Robôs' },
    { src: '/media/video-filtro-otimizado-v2.mp4', poster: '/media/thumb-filtro.png', tag: 'Projeto Maker', title: 'Filtro de Laranjas' },
    { src: '/media/video-tirolesa-otimizado-v2.mp4', poster: '/media/thumb-tirolesa2.jpg', tag: 'Engenharia', title: 'Tirolesa Maker' },
  ];

  const methods = [
    {
      num: '01',
      title: 'Pensamento Lógico',
      icon: 'fa-brain',
      content: {
        title: 'Lógica & Algoritmos',
        desc: 'Antes de programar, é preciso pensar. Ensinamos o aluno a estruturar problemas complexos em passos lógicos simples.',
        tags: ['Programação Iconográfica', 'Lógica', 'Algoritmos'],
        img: '/media/foto-scratch.jpg',
      },
    },
    {
      num: '02',
      title: 'Cultura Maker',
      icon: 'fa-hands',
      content: {
        title: 'Mãos na Massa',
        desc: 'Aprender fazendo. Projetos práticos que desenvolvem criatividade e habilidades manuais.',
        tags: ['Prototipagem', 'DIY', 'Ferramentas'],
        img: '/media/foto-montagem.jpg',
      },
    },
    {
      num: '03',
      title: 'Trabalho em Equipe',
      icon: 'fa-users',
      content: {
        title: 'Colaboração',
        desc: 'Projetos em grupo para aprender a comunicar ideias e trabalhar coletivamente.',
        tags: ['Equipe', 'Comunicação', 'Liderança'],
        img: '/media/foto-equipe.jpg',
      },
    },
    {
      num: '04',
      title: 'Robótica Educacional',
      icon: 'fa-robot',
      content: {
        title: 'Automação & IA',
        desc: 'Do básico ao avançado: montagem de robôs e programação de comportamentos inteligentes.',
        tags: ['Robótica', 'Eletrônica', 'IA Básica'],
        img: '/media/foto-robo.jpg',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-[#0F1822] text-white overflow-x-hidden font-sans relative">
      {/* Pattern Background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/media/pattern.png)',
          backgroundSize: '500px',
          backgroundRepeat: 'repeat',
          opacity: 0.08,
          zIndex: 1
        }}
      />
      
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0F1822]/80 border-b border-[#A4D7D2]/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <img src="/media/logo-creative.png" alt="Creative Solution" className="h-8 w-auto" />
          <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#D5DFE3]">
            <a href="#metodologia" onClick={(e) => scrollToSection(e, 'metodologia')} className="hover:text-[#A4D7D2] transition-colors">Metodologia</a>
            <a href="#galeria" onClick={(e) => scrollToSection(e, 'galeria')} className="hover:text-[#A4D7D2] transition-colors">Galeria</a>
            <a href="#contato" onClick={(e) => scrollToSection(e, 'contato')} className="hover:text-[#A4D7D2] transition-colors">Contato</a>
          </div>
          <button className="md:hidden text-[#D5DFE3] hover:text-[#A4D7D2]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden pb-6 px-6 flex flex-col gap-6 text-center text-[#D5DFE3]">
            <a href="#metodologia" onClick={(e) => scrollToSection(e, 'metodologia')} className="hover:text-[#A4D7D2] transition-colors">Metodologia</a>
            <a href="#galeria" onClick={(e) => scrollToSection(e, 'galeria')} className="hover:text-[#A4D7D2] transition-colors">Galeria</a>
            <a href="#contato" onClick={(e) => scrollToSection(e, 'contato')} className="hover:text-[#A4D7D2] transition-colors">Contato</a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <header className="min-h-screen flex items-center relative overflow-hidden pt-20 z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(164,215,210,0.08),transparent)]" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <FadeUp isVisible={visibleElements.has(0)} fadeRef={el => fadeRefs.current[0] = el}>
            <span className="text-sm font-medium text-[#A4D7D2] tracking-widest uppercase mb-6 block">Bem-vindo à</span>
            <h1 className="text-6xl md:text-8xl font-bold leading-none mb-8">
              Creative<br />
              Solution
            </h1>
            <p className="text-xl md:text-2xl text-[#9FA4B1] mb-12 max-w-2xl leading-relaxed">
              Educação tecnológica humanizada. Formando os líderes do amanhã através de robótica, programação e cultura maker.
            </p>
            <a href="#contato" onClick={(e) => scrollToSection(e, 'contato')} className="px-10 py-5 rounded-xl text-lg font-semibold bg-[#A4D7D2] text-[#0F1822] hover:bg-[#8ac4be] transition-all">
              Fale Conosco
            </a>
          </FadeUp>
        </div>
      </header>

      {/* Metodologia */}
      <section id="metodologia" className="py-32 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp isVisible={visibleElements.has(5)} fadeRef={el => fadeRefs.current[5] = el}>
            <span className="text-sm font-medium text-[#A4D7D2] tracking-widest uppercase mb-6 block">Nossa Abordagem</span>
            <h2 className="text-4xl md:text-6xl font-bold mb-20">Metodologia Inovadora</h2>
            <div className="coverflow-container mb-24">
              <div className="coverflow-wrapper">
                {slides.map((slide, i) => (
                  <div
                    key={i}
                    className={`coverflow-slide ${i === currentSlide ? 'coverflow-center' :
                      i === (currentSlide - 1 + totalSlides) % totalSlides ? 'coverflow-left' :
                        i === (currentSlide + 1) % totalSlides ? 'coverflow-right' : 'coverflow-hidden'
                      }`}
                    onClick={() => i === currentSlide && setLightbox({ open: true, src: slide.img, type: 'image' })}
                  >
                    <img src={slide.img} alt={slide.label} />
                    {i === currentSlide && (
                      <div className="coverflow-label">
                        <span className="coverflow-tag">{slide.label}</span>
                        <p className="coverflow-desc">{slide.desc}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="coverflow-indicators">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    className={`coverflow-dot ${currentSlide === i ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(i)}
                  />
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-12 gap-8 items-start">
              {/* Cards verticais à esquerda */}
              <div className="md:col-span-4 flex flex-col gap-4">
                {methods.map((method, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveMethod(i)}
                    className={`p-5 rounded-2xl backdrop-blur-xl text-left border ${activeMethod === i ? 'bg-[#A4D7D2]/20 border-[#A4D7D2]/30' : 'bg-[#022895]/20 border-[#A4D7D2]/10'} hover:border-[#A4D7D2]/30 transition-all`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-bold text-[#A4D7D2]/50">{method.num}</span>
                      <i className={`fas ${method.icon} text-xl text-[#A4D7D2]`}></i>
                      <h3 className="text-lg font-bold">{method.title}</h3>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Card de conteúdo à direita */}
              <div className="md:col-span-8">
                <div className="rounded-2xl border border-[#A4D7D2]/10 bg-[#022895]/10 backdrop-blur-xl overflow-hidden flex flex-col md:flex-row">
                  {/* Imagem à esquerda do card */}
                  <div className="md:w-1/2 h-64 md:h-auto overflow-hidden">
                    <img
                      src={methods[activeMethod].content.img}
                      alt={methods[activeMethod].title}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
                      onClick={() => setLightbox({ open: true, src: methods[activeMethod].content.img, type: 'image' })}
                    />
                  </div>
                  
                  {/* Conteúdo à direita do card */}
                  <div className="md:w-1/2 p-6 flex flex-col justify-center">
                    <h3 className="text-2xl font-bold mb-3">{methods[activeMethod].content.title}</h3>
                    <p className="text-[#9FA4B1] mb-5 text-sm leading-relaxed">{methods[activeMethod].content.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {methods[activeMethod].content.tags.map((tag, j) => (
                        <span key={j} className="px-3 py-1.5 rounded-full bg-[#022895]/40 text-[#A4D7D2] text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Counters */}
      <section className="py-20 bg-[#022895]/5 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp isVisible={visibleElements.has(10)} fadeRef={el => fadeRefs.current[10] = el}>
            <CountersSection />
          </FadeUp>
        </div>
      </section>

      {/* Galeria */}
      <section id="galeria" className="py-32 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp isVisible={visibleElements.has(15)} fadeRef={el => fadeRefs.current[15] = el}>
            <span className="text-sm font-medium text-[#A4D7D2] tracking-widest uppercase mb-6 block">Nossos Trabalhos</span>
            <h2 className="text-4xl md:text-6xl font-bold mb-12">Galeria</h2>
            <VideosGrid videos={videos} />
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative z-10 bg-[radial-gradient(circle_at_50%_50%,rgba(164,215,210,0.05),transparent)]">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <FadeUp isVisible={visibleElements.has(20)} fadeRef={el => fadeRefs.current[20] = el}>
            <span className="text-sm font-medium text-[#A4D7D2] tracking-widest uppercase mb-6 block">Próximo Passo</span>
            <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              Vamos construir o<br />
              <span className="text-[#A4D7D2]">futuro juntos?</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/5554991434219" target="_blank" rel="noopener noreferrer" className="px-10 py-5 rounded-xl text-lg font-semibold bg-[#A4D7D2] text-[#0F1822] hover:bg-[#8ac4be] transition-all">
                Agendar Reunião
              </a>
              <a href="https://wa.me/5554991434219" target="_blank" rel="noopener noreferrer" className="px-10 py-5 rounded-xl text-lg border border-[#9FA4B1]/30 text-[#D5DFE3] hover:border-[#A4D7D2] hover:text-[#A4D7D2] transition-all flex items-center justify-center gap-3">
                <i className="fab fa-whatsapp text-[#A4D7D2]"></i> (54) 99143-4219
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <footer id="contato" className="py-20 border-t border-[#A4D7D2]/10 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          {/* <div className="grid md:grid-cols-2 gap-16 mb-16">
            <div>
              <img src="/media/logo-creative.png" alt="Creative Solution" className="h-12 w-auto mb-8" />
              <p className="text-[#9FA4B1] mb-8 max-w-md">Educação tecnológica humanizada. Formando os líderes do amanhã.</p>
              <div className="flex gap-4">
                {[
                  { icon: 'fa-instagram', href: 'https://instagram.com/creativesolution' },
                  { icon: 'fa-facebook-f', href: '#' },
                  { icon: 'fa-youtube', href: '#' },
                ].map((social, i) => (
                  <a key={i} href={social.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#022895]/20 backdrop-blur border border-[#A4D7D2]/10 rounded-lg flex items-center justify-center text-[#9FA4B1] hover:text-[#A4D7D2] hover:border-[#A4D7D2]/30 transition-all">
                    <i className={`fab ${social.icon}`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div> */}

          <div className="pt-8 border-t border-[#A4D7D2]/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#9FA4B1]">
            <p>© 2025 Creative Solution. Todos os direitos reservados.</p>
            <span>comercial@creativesolution.net.br</span>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      {lightbox.open && (
        <div
          className="fixed inset-0 bg-[#0F1822]/98 z-[1000] flex items-center justify-center"
          onClick={() => setLightbox({ open: false, src: '', type: '' })}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setLightbox({ open: false, src: '', type: '' })}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-[#A4D7D2]/20 hover:bg-[#A4D7D2] flex items-center justify-center transition-colors text-white hover:text-[#0F1822]"
            >
              <i className="fas fa-times"></i>
            </button>
            {lightbox.type === 'video' ? (
              <video src={lightbox.src} autoPlay muted playsInline className="max-w-full max-h-[85vh] rounded-xl border border-[#A4D7D2]/20" />
            ) : (
              <img src={lightbox.src} alt="" className="max-w-full max-h-[85vh] rounded-xl border border-[#A4D7D2]/20" />
            )}
          </div>
        </div>
      )}

      {/* Estilos globais */}
      <style>{`
        @font-face {
          font-family: 'Clash Grotesk';
          src: url('/fonts/ClashGrotesk-Light.otf') format('opentype');
          font-weight: 300;
        }
        @font-face {
          font-family: 'Clash Grotesk';
          src: url('/fonts/ClashGrotesk-Regular.otf') format('opentype');
          font-weight: 400;
        }
        @font-face {
          font-family: 'Clash Grotesk';
          src: url('/fonts/ClashGrotesk-Medium.otf') format('opentype');
          font-weight: 500;
        }
        @font-face {
          font-family: 'Clash Grotesk';
          src: url('/fonts/ClashGrotesk-Semibold.otf') format('opentype');
          font-weight: 600;
        }
        @font-face {
          font-family: 'Clash Grotesk';
          src: url('/fonts/ClashGrotesk-Bold.otf') format('opentype');
          font-weight: 700;
        }
        
        body, .font-sans {
          font-family: 'Clash Grotesk', sans-serif;
        }
        
        /* Coverflow Carousel */
        .coverflow-container {
          position: relative;
          height: 380px;
          perspective: 1200px;
          overflow: visible;
        }
        
        .coverflow-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
        }
        
        .coverflow-slide {
          position: absolute;
          width: 340px;
          height: 240px;
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.6);
          border: 1px solid rgba(164, 215, 210, 0.2);
          backface-visibility: hidden;
        }
        
        .coverflow-slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        /* Center slide - Main focus */
        .coverflow-center {
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) rotateY(0deg) scale(1);
          z-index: 100;
          opacity: 1;
          cursor: pointer;
        }
        
        /* Left slide - Rotated and behind */
        .coverflow-left {
          left: 0;
          top: 50%;
          transform: translateY(-50%) translateX(-30%) rotateY(45deg) scale(0.85);
          z-index: 50;
          opacity: 0.6;
          cursor: default;
        }
        
        /* Right slide - Rotated and behind */
        .coverflow-right {
          right: 0;
          left: auto;
          top: 50%;
          transform: translateY(-50%) translateX(30%) rotateY(-45deg) scale(0.85);
          z-index: 50;
          opacity: 0.6;
          cursor: default;
        }
        
        /* Hidden slides */
        .coverflow-hidden {
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) scale(0.5);
          z-index: 1;
          opacity: 0;
          pointer-events: none;
        }
        
        /* Label on center slide */
        .coverflow-label {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 20px;
          background: linear-gradient(to top, rgba(15, 24, 34, 0.95) 0%, rgba(15, 24, 34, 0.7) 50%, transparent 100%);
        }
        
        .coverflow-tag {
          display: block;
          font-size: 11px;
          color: #A4D7D2;
          font-family: monospace;
          margin-bottom: 4px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }
        
        .coverflow-desc {
          font-size: 16px;
          color: white;
          font-weight: 600;
          margin: 0;
        }
        
        /* Indicators */
        .coverflow-indicators {
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 200;
        }
        
        .coverflow-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(159, 164, 177, 0.3);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .coverflow-dot:hover {
          background: rgba(159, 164, 177, 0.5);
        }
        
        .coverflow-dot.active {
          width: 24px;
          border-radius: 12px;
          background: #A4D7D2;
        }
        
        /* Mobile adjustments */
        @media (max-width: 768px) {
          .coverflow-container {
            height: 300px;
          }
          
          .coverflow-slide {
            width: 280px;
            height: 200px;
          }
          
          .coverflow-left {
            transform: translateY(-50%) translateX(-50%) rotateY(45deg) scale(0.7);
            opacity: 0.4;
          }
          
          .coverflow-right {
            transform: translateY(-50%) translateX(50%) rotateY(-45deg) scale(0.7);
            opacity: 0.4;
          }
        }
      `}</style>
    </div>
  );
}